/**
 * Vertex shader source code for the assistant animation.
 * Maps vertex positions to screen coordinates and passes position data to fragment shader.
 * @type {string}
 */
const vertexShaderSource = `
      attribute vec2 a_position;
      varying vec2 v_position;

      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        v_position = a_position;
      }
    `;

/**
 * GLSL function that generates dynamic colors based on position and time.
 * Creates a tech-style animated color pattern with waves and gradients.
 * @type {string}
 */
const fragmentShaderGetColor = `vec3 getColor(vec2 pos, float time) {
    float x = pos.x * 30.0;
    float y = pos.y * 30.0;

    float r = 0.1 + 0.6 * cos((x * x - y * y) / 300.0 + time);
    float g = 0.2 + 0.5 * sin((x * x * cos(time / 4.0) + y * y * sin(time / 3.0)) / 200.0);
    float b = 0.3 + 0.4 * sin(5.0 * sin(time / 9.0) + ((x - 100.0) * (x - 100.0) + (y - 100.0) * (y - 100.0)) / 1100.0);

    vec3 baseColor = vec3(r, g, b);

    float wave1 = sin(time * 2.0 + pos.x * 10.0) * 0.3;
    float wave2 = cos(time * 1.5 + pos.y * 8.0) * 0.3;

    vec3 cyan = vec3(0.0, 0.6, 0.8);
    vec3 magenta = vec3(0.8, 0.0, 0.6);
    vec3 electricBlue = vec3(0.0, 0.2, 0.8);

    vec3 techColor = mix(mix(cyan, magenta, wave1 + 0.5), electricBlue, wave2 + 0.5);

    // Boost saturation and brightness
    vec3 finalColor = mix(baseColor, techColor, 0.6);
    finalColor = pow(finalColor, vec3(0.8)); // Gamma correction for brightness
    finalColor = clamp(finalColor * 1.2, 0.0, 1.0); // Increase overall brightness

    return finalColor;
}
`;

/**
 * GLSL code for rendering only the frame border of the animation.
 * Draws color only within the frame width and discards pixels in the center.
 * @type {string}
 */
const isFrameFragmentShader = `
    vec2 pixelPos = uv * u_resolution;

    bool isFrame = (pixelPos.x < u_frameWidth) ||
                    (pixelPos.x > u_resolution.x - u_frameWidth) ||
                    (pixelPos.y < u_frameWidth) ||
                    (pixelPos.y > u_resolution.y - u_frameWidth);

    if (isFrame) {
        vec3 color = getColor(uv, u_time);
        gl_FragColor = vec4(color, 1.0);
    } else {
        discard;
    }
`;

/**
 * GLSL code for rendering the full animation without frame restrictions.
 * Applies color to all pixels in the canvas area.
 * @type {string}
 */
const isNotFrameFragmentShader = `
    vec3 color = getColor(uv, u_time);
    gl_FragColor = vec4(color, 1.0);
`;

/**
 * Generates a complete fragment shader source code for the assistant animation.
 * @param {boolean} isFrame - If true, renders only the frame border; if false, renders full area
 * @returns {string} Complete fragment shader source code
 */
const fragmentShaderSource = (isFrame = false) => `
    precision mediump float;
    uniform float u_time;
    uniform vec2 u_resolution;
    varying vec2 v_position;
    ${isFrame ? 'uniform float u_frameWidth;' : ''}

    ${fragmentShaderGetColor}

    void main() {
        vec2 uv = (v_position + 1.0) / 2.0;
        ${isFrame ? isFrameFragmentShader : isNotFrameFragmentShader}
    }
  `;

/**
 * @fileoverview WebGL shader code for the Search Assistant animation component.
 * Provides vertex and fragment shaders for creating dynamic, animated visual effects
 * with optional frame-only rendering mode.
 */

export { fragmentShaderSource, vertexShaderSource };
